import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building, Users, Zap, Shield, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSiteConfigValue } from "@/hooks/useSiteConfigValue";
import SEO from "@/components/SEO";
import { createBreadcrumbSchema } from "@/components/SEO";

const EnterpriseDemo = () => {
  const { value: googleFormUrl } = useSiteConfigValue('google_form_enterprise', '');

  const handleDemoRequest = () => {
    if (googleFormUrl) {
      window.open(googleFormUrl, '_blank');
    } else {
      window.open('https://forms.google.com/your-enterprise-form', '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="Enterprise Demo | AI Bulk Image Generator for Teams"
        description="Schedule a personalized enterprise demo of The Bulk Image Generator. See how AI image generation can scale your business with custom solutions and dedicated support."
        keywords="enterprise demo, business ai images, team image generation, bulk ai solution, enterprise ai platform, custom ai solutions, dedicated support"
        url="/enterprise-demo"
        jsonLd={[
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Enterprise Demo', url: '/enterprise-demo' }
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'The Bulk Image Generator Enterprise',
            description: 'Enterprise AI image generation with custom solutions, dedicated support, and team collaboration.',
            brand: { '@type': 'Brand', name: 'The Bulk Image Generator' },
            offers: {
              '@type': 'Offer',
              price: '99',
              priceCurrency: 'USD',
              priceValidUntil: '2026-12-31',
              availability: 'https://schema.org/InStock',
              description: 'Custom enterprise pricing available'
            }
          }
        ]}
      />
      <Header />
      
      <main className="pt-20">
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Enterprise Demo
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Schedule Your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Enterprise Demo</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Discover how The Bulk Image Generator can transform your organization's workflow
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Request Enterprise Demo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center py-8">
                    <div className="mb-6">
                      <Building className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">
                        Secure Enterprise Demo Request
                      </h3>
                      <p className="text-gray-300 mb-6 max-w-md mx-auto">
                        To protect your business information and ensure a tailored experience, 
                        we use a secure external form for enterprise demo requests.
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleDemoRequest}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 text-lg font-semibold"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Open Demo Request Form
                    </Button>
                    
                    <div className="mt-6 text-sm text-gray-400">
                      <Shield className="w-4 h-4 inline mr-1" />
                      Secure and encrypted form processing
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">What You'll Get</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-blue-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Custom Demo</h4>
                      <p className="text-gray-400 text-sm">Tailored to your specific use case</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Security Overview</h4>
                      <p className="text-gray-400 text-sm">Learn about our enterprise security features</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="w-5 h-5 text-purple-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Team Training</h4>
                      <p className="text-gray-400 text-sm">Onboarding and training resources</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Building className="w-5 h-5 text-yellow-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium">Custom Pricing</h4>
                      <p className="text-gray-400 text-sm">Pricing tailored to your organization</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Typical Demo Duration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 mb-2">30-45 minutes</p>
                  <p className="text-gray-400 text-sm">Includes Q&A session and next steps discussion</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Enhanced Security</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 text-sm mb-2">
                    We've implemented enhanced security measures to protect your business data:
                  </p>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• External secure form processing</li>
                    <li>• No business data storage on our servers</li>
                    <li>• Encrypted data transmission</li>
                    <li>• GDPR compliant handling</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EnterpriseDemo;